/**
 * Abschnitt „Betriebsmodi und Schallemissionen" (ELiA-Formulare 16.1.10 und 4.5)
 *
 * Ebenfalls eine Liste: je Modus 11 vermessene Werte, an der Anlage im Projekt vier weitere aus
 * dem Betrieb. Die Tabelle zeigt Bezeichnung, Drehzahl und Gesamtpegel; die acht Oktavbänder
 * stehen im Zeilendialog – nebeneinander waeren sie acht kaum lesbare Spalten.
 *
 * Wie die Stoffliste kennt die Komponente ihren Elternteil nicht, sondern bekommt Zeilen und
 * Rückrufe. Der Schalter `mitBetriebsangaben` unterscheidet Vorlage und Kopie.
 */
import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SubSection from "../../../layout/SubSection";
import BaseTable, { TableColumn } from "../../../tables/common/BaseTable";
import Button from "../../../common/inputs/Button";
import BetriebsmodusDialog, {
  BetriebsangabenWerte,
} from "./BetriebsmodusDialog";
import {
  BetriebsmodusCreate,
  BetriebsmodusFelder,
  BetriebsmodusUpdate,
} from "../../../../services/api/anlagentypen";
import { WertelistenOptions } from "../hooks/useWertelisten";
import { colors, fonts } from "../../../../styles/theme";

/** Wie bei der Stoffliste: dieselben Felder an Vorlage und Kopie, nur der Besitzer wechselt. */
export type BetriebsmodusZeile = BetriebsmodusFelder &
  Partial<BetriebsangabenWerte> & { id: string };

const SPALTEN_VORLAGE: TableColumn[] = [
  { key: "bezeichnung", label: "Betriebsmodus", width: "40%" },
  {
    key: "rotordrehzahl_pro_min",
    label: "Drehzahl",
    align: "right",
    unit: "1/min",
  },
  {
    key: "schallleistungspegel",
    label: "Gesamtpegel",
    align: "right",
    unit: "dB(A)",
  },
];

interface BetriebsmodusListeSectionProps {
  modi: BetriebsmodusZeile[];
  wertelisten: WertelistenOptions;
  loading?: boolean;
  /** An der Anlage an: Einsatzzeiten und die Frage, ob der Modus gefahren wird */
  mitBetriebsangaben?: boolean;
  /** An der Anlage nur lesbar – die Pegel stammen aus der Vermessung des Anlagentyps */
  vermessungReadOnly?: boolean;
  hinweisStattListe?: string;
  onCreate?: (werte: BetriebsmodusCreate) => Promise<void>;
  onUpdate: (
    modusId: string,
    werte: BetriebsmodusUpdate & BetriebsangabenWerte,
  ) => Promise<void>;
  onDelete?: (modusId: string) => Promise<void>;
  defaultExpanded?: boolean;
}

const BetriebsmodusListeSection: React.FC<BetriebsmodusListeSectionProps> = ({
  modi,
  wertelisten,
  loading = false,
  mitBetriebsangaben = false,
  vermessungReadOnly = false,
  hinweisStattListe,
  onCreate,
  onUpdate,
  onDelete,
  defaultExpanded = false,
}) => {
  const [dialogOffen, setDialogOffen] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<BetriebsmodusZeile | null>(null);

  // Die Einsatzzeiten stehen im Zeilendialog: In der Übersicht wären sie drei weitere Spalten
  // für eine Angabe, die bei einem Windpark meist für alle Modi gleich lautet.
  const spalten = SPALTEN_VORLAGE;

  const oeffnen = (modus: BetriebsmodusZeile | null) => {
    setBearbeitet(modus);
    setDialogOffen(true);
  };

  return (
    <SubSection
      title="Betriebsmodi und Schallemissionen"
      defaultExpanded={defaultExpanded}
      actions={
        !hinweisStattListe &&
        onCreate && (
          <Button startIcon={<AddIcon />} onClick={() => oeffnen(null)}>
            Betriebsmodus anlegen
          </Button>
        )
      }
    >
      {hinweisStattListe ? (
        <Typography
          sx={{
            fontFamily: fonts.primary,
            fontSize: "0.875rem",
            color: colors.text.secondary,
          }}
        >
          {hinweisStattListe}
        </Typography>
      ) : (
        <Box>
          <BaseTable
            columns={spalten}
            data={modi}
            loading={loading}
            fixedLayout
            emptyMessage="Noch keine Betriebsmodi erfasst."
            onRowClick={(row) =>
              oeffnen(modi.find((m) => m.id === row.id) ?? null)
            }
            onDelete={onDelete ? (row) => void onDelete(row.id) : undefined}
          />
          <BetriebsmodusDialog
            open={dialogOffen}
            modus={bearbeitet}
            mitBetriebsangaben={mitBetriebsangaben}
            vermessungReadOnly={vermessungReadOnly}
            wertelisten={wertelisten}
            onClose={() => setDialogOffen(false)}
            onSubmit={async (werte) => {
              if (bearbeitet) {
                await onUpdate(bearbeitet.id, werte);
              } else if (onCreate) {
                await onCreate(werte);
              }
            }}
          />
        </Box>
      )}
    </SubSection>
  );
};

export default BetriebsmodusListeSection;
